import { Component } from "react";
import { graphql } from "gatsby";
import styles from "./product-block.module.less";

import QuantitySelector from "components/block-elements/quantity-selector/quantity-selector";
import AnimationPop from "components/block-elements/animation-pop/animation-pop";
import { ProductInfo } from "./components";

import UiBkgImage from "@rnb/ui-bkg-image";

import ProductPageCalendar from "components/block-elements/product-page-calendar/product-page-calendar";

import SiteConfigurationStore from "components/stores/site-configuration-store";

import { ProductCalendarHelper } from "components/helpers";
import {
  DateHelper,
  NotificationHelper,
  ECommerceHelper,
  JetshopHelper,
  extract,
  isArray,
  createDate,
  CartContext,
  ProductCalendarService,
  ProductStore,
  OpeningHoursService,
  JetshopStore,
  PrimaryButtonLink,
  PrimaryButton,
  SelectBox,
  useShopServiceCartContext,
} from "@parksandresorts/core";
import { GlobalContextConsumer } from "components/stores/global-context";
import SpeechBubbleProductListing from "components/block-elements/speech-bubble-product-listing/speech-bubble-product-listing";

const ShopServiceCartContextConsumer = ({ children }) => {
  const context = useShopServiceCartContext();
  return (
    <GlobalContextConsumer>
      {(globalContext) => children({ shopServiceContext: context, globalContext })}
    </GlobalContextConsumer>
  );
};

export default class ProductBlock extends Component {
  constructor(props) {
    super(props);

    this.productName = extract(props, "productName");
    this.productImage = extract(props, "productImage.file.url");
    this.label = extract(props, "label");
    this.lableComponentTheme = extract(props, "labelComponentTheme");
    this.productData = extract(props, "productData");
    this.description = extract(props, "description.childMarkdownRemark.html");
    this.componentTheme = extract(props, "componentTheme");
    this.readMoreLabel = extract(props, "readMoreLabel");
    this.readMoreBody = extract(props, "readMoreText.childMarkdownRemark.html");
    this.jetshopId = extract(this.productData, "jetshopId");
    this.productType = extract(this.productData, "productType");
    this.blockInOverlay = extract(props, "blockInOverlay");
    this.jetshopConfig = ECommerceHelper.getJetshopConfig();
    this.addedToCartHeadline = extract(props, "addedToCartHeadline", "Hurra! 🥳 🎉");
    this.addedToCartBody = extract(props, "addedToCartBody", "Produkten är nu tillagd i varukorgen!");
    this.customPriceText = extract(props, "customPriceText");
    this.customCtaText = extract(props, "customCtaText");
    this.optionalLinkLabelText = extract(props, "optionalLinkLabelText");
    this.optionalLinkUrl = extract(props, "optionalLinkUrl");
    this.activateProductCalendar = extract(props, "activateProductCalendar");
    this.preLoadedProduct = extract(props, "locationState.product");
    this.preSelectedProductDate = extract(props, "locationState.selectedProductDate");

    this.totalValueLabel = SiteConfigurationStore.getLabel("TOTAL_VALUE");
    this.selectDayForVisitLabel = SiteConfigurationStore.getLabel("PRODUCT_SELECT_DAY_FOR_VISIT");
    this.calendarOptOutLabel = SiteConfigurationStore.getLabel("PRODUCT_CALENDAR_OPT_OUT");
    this.calendarOptInLabel = SiteConfigurationStore.getLabel("PRODUCT_CALENDAR_OPT_IN");
    this.chooseDayToContinueLabel = SiteConfigurationStore.getLabel("PRODUCT_CHOSE_DAY_TO_CONTINUE");
    this.openTicketLabel = SiteConfigurationStore.getLabel("PRODUCT_OPEN_TICKET");
    this.openTicketTextLabel = SiteConfigurationStore.getLabel("PRODUCT_OPEN_TICKET_TEXT");
    this.selectedDateLabel = SiteConfigurationStore.getLabel("SELECTED_DATE");
    this.selectOtherDateLabel = SiteConfigurationStore.getLabel("PRODUCT_SELECT_OTHER_DAY_FOR_VISIT");
    this.numberOfJetPassHeadline = SiteConfigurationStore.getLabel(
      "PRODUCT_NUMBER_OF_JETPASS_HEADLINE",
      "JetPass ingår",
    );
    this.numberOfJetPassParagraph = SiteConfigurationStore.getLabel("PRODUCT_NUMBER_OF_JETPASS_PARAGRAPH");
    this.noJetPassIncludedText = SiteConfigurationStore.getLabel("PRODUCT_NO_JETPASS_INCLUDED");
    this.soldOutLabel = SiteConfigurationStore.getLabel("PRODUCT_SOLD_OUT_SHORT", "Slut");
    this.upcomingReleaseLabel = SiteConfigurationStore.getLabel("PRODUCT_UPCOMING_RELEASE", "Släpps");
    this.serviceErrorText = extract(props, "serviceErrorText");

    this.chooseAmountLabel = this.jetshopConfig.chooseAmountLabel;
    this.addToCartLabel = this.jetshopConfig.addToCartLabel;
    this.currencyDisplayName = this.jetshopConfig.currencyDisplayName;

    this.today = createDate();
    this.today.setHours(0, 0, 0, 0);

    this.baseProduct = {
      jetshopId: this.jetshopId,
      glkId: this.productData ? this.productData.glkId : undefined,
      productType: this.productType,
      allowFlexibleProduct: this.productType === JetshopHelper.ProductTypes.Standard,
    };

    this.transformedPreLoadedProduct = {
      jetshopId: this.preLoadedProduct ? extract(this.preLoadedProduct, "productData.jetshopId") : null,
      glkId: this.preLoadedProduct ? extract(this.preLoadedProduct, "productData.glkId") : null,
      productType: this.preLoadedProduct ? extract(this.preLoadedProduct, "productData.productType") : null,
      attributes: this.preLoadedProduct ? this.preLoadedProduct.attributes : null,
      price: this.preLoadedProduct ? this.preLoadedProduct.price : null,
      previousPrice: this.preLoadedProduct ? this.preLoadedProduct.previousPrice : null,
      stockLevel: this.preLoadedProduct ? this.preLoadedProduct.stockLevel : null,
      isBuyable: this.preLoadedProduct ? this.preLoadedProduct.isBuyable : null,
      jetshopName: this.preLoadedProduct ? this.preLoadedProduct.jetshopName : null,
      timeSlots: this.preLoadedProduct ? this.preLoadedProduct.timeSlots : null,
      numberOfJetpassIncluded: this.preLoadedProduct ? this.preLoadedProduct.numberOfJetpassIncluded : null,
      numberOfEntryTicketsIncluded: this.preLoadedProduct ? this.preLoadedProduct.numberOfEntryTicketsIncluded : null,
    };

    this.onClickDate = this.onClickDate.bind(this);
    this.onChangeMonth = this.onChangeMonth.bind(this);
    this.onChangeAttribute = this.onChangeAttribute.bind(this);
    this.createAddedToCartNotification = this.createAddedToCartNotification.bind(this);

    this.state = {
      quantity: 1,
      isAddingToCart: false,
      productDataLoaded: this.preLoadedProduct ? true : false,
      flexibleTicketsSelected: false,
      bookableProductsLoaded: false,
      selectedProduct: this.preLoadedProduct
        ? this.transformProductToCartReadyProduct(this.transformedPreLoadedProduct)
        : null,
      selectedDate: this.preSelectedProductDate ? this.preSelectedProductDate : null,
      currentDisplayedMonth: this.preSelectedProductDate ? this.preSelectedProductDate : null,
      useBaseProduct: !this.activateProductCalendar,
      usePreSelectedDate: this.preSelectedProductDate ? true : false,
      showServiceError: false,
      extendedProduct: null,
    };
  }

  componentDidMount() {
    this.__isMounted = true;

    JetshopStore.getExtendedProductByGlkId(Number(this.productData.glkId))
      .then((res) => {
        if (!this.__isMounted) {
          return;
        }

        this.baseProduct = {
          ...this.baseProduct,
          attributes: res.attributes,
          price: res.price,
          previousPrice: res.previousPrice,
          stockLevel: res.stockLevel,
          isBuyable: res.isBuyable,
          jetshopName: res.jetshopName,
          minimumQuantity: res.minimumQuantity,
        };

        let selectedProduct = null;

        if (isArray(this.baseProduct.attributes)) {
          const firstAttribute = this.baseProduct.attributes.find((attr) => attr.isBuyable);
          if (firstAttribute) {
            selectedProduct = {
              ...this.baseProduct,
              price: firstAttribute.price,
              attributeId: firstAttribute.attributeId,
            };
          }
        }

        this.setState({
          productDataLoaded: true,
          selectedProduct: selectedProduct,
          quantity: res.minimumQuantity ? res.minimumQuantity : 1,
          extendedProduct: res,
        });
      })
      .catch((error) => console.log(error));

    if (this.activateProductCalendar) {
      const openingHoursPromise = OpeningHoursService.getAllDates();
      const productCalendarPromise = ProductCalendarService.getProductsCalendar(this.baseProduct.glkId);

      Promise.all([openingHoursPromise, productCalendarPromise])
        .then((response) => {
          if (!this.__isMounted) {
            return;
          }

          const [openingHours, productCalendar] = response;

          const openingHoursCalendar = this.transformOpeningHoursToCalendarData(openingHours);
          const filteredProductCalendar = ProductCalendarHelper.filterProductCalendar(productCalendar);

          const transformedProductCalendarData = this.transformProductCalendarToCalendarData(filteredProductCalendar);
          return Promise.all([Promise.resolve(openingHoursCalendar), Promise.resolve(transformedProductCalendarData)]);
        })
        .then((response) => {
          const [openingHoursCalendar, transformedProductCalendar] = response;

          const firstMonthWithCalendarData =
            transformedProductCalendar instanceof Array && transformedProductCalendar.length > 0
              ? transformedProductCalendar[0].date
              : this.today;
          let currentDisplayedMonth = this.state.currentDisplayedMonth;
          if (!currentDisplayedMonth) {
            // If unset, set current month to first product calendar date OR today if today is later
            currentDisplayedMonth = DateHelper.isGreaterThanYYYYMM(this.today, firstMonthWithCalendarData)
              ? this.today
              : firstMonthWithCalendarData;
          }

          const calendarData = this.mergeCalendarWithProductCalendar(openingHoursCalendar, transformedProductCalendar);
          this.setState({
            bookableProductsLoaded: true,
            bookableProducts: calendarData,
            currentDisplayedMonth: currentDisplayedMonth,
          });
        })
        .catch((error) => {
          console.log(error);
          if (!this.__isMounted) {
            return;
          }

          this.setState({
            bookableProductsLoaded: true,
            showServiceError: true,
          });
        });
    }
  }

  componentWillUnmount() {
    this.__isMounted = false;
  }

  transformOpeningHoursToCalendarData(response) {
    return response.map((dateItem) => {
      const dateFrom = createDate(dateItem.opens);
      const dateTo = createDate(dateItem.closing);
      const calendarItem = {
        date: dateFrom,
        openingTime: DateHelper.toStringTimeHHMM(dateFrom),
        closingTime: DateHelper.toStringTimeHHMM(dateTo),
        open: true,
      };

      return calendarItem;
    });
  }

  transformProductCalendarToCalendarData(productCalendar) {
    if (!(productCalendar instanceof Array && productCalendar.length > 0)) {
      return null;
    }

    const extendedProductsCalendar = [];

    for (const productDate of productCalendar) {
      const extendedProducts = [];
      if (productDate.products instanceof Array && productDate.products.length > 0) {
        for (const product of productDate.products) {
          if (product) {
            const extended = ProductStore.getProductByJetshopId(product.jetshopId);

            if (extended) {
              extendedProducts.push({
                ...product,
                numberOfEntryTicketsIncluded: extended.numberOfEntryTicketsIncluded,
                numberOfJetpassIncluded: extended.numberOfJetpassIncluded,
              });
            } else {
              extendedProducts.push(product);
            }
          }
        }
      }

      extendedProductsCalendar.push({ date: productDate.date, products: extendedProducts });
    }

    return extendedProductsCalendar.map((productDate) => {
      const product = JetshopHelper.getLowestPricedProduct(productDate.products);
      const soldOut = JetshopHelper.isDateSoldOut([product]);
      return {
        date: createDate(productDate.date),
        product: product,
        soldOut: soldOut,
        open: true,
        dateContent: !product ? this.soldOutLabel : null,
        customClassName: !product || soldOut ? styles.soldOut : null,
      };
    });
  }

  mergeCalendarWithProductCalendar(calendarData, productCalendar) {
    const mergedCalendar = [];
    const openDateWithNoProducts = {
      dateContent: this.upcomingReleaseLabel,
      customClassName: styles.openDateWithNoProducts,
    };
    for (const calendarDate of calendarData) {
      const productForDate = productCalendar.find((productDate) =>
        DateHelper.isEqualYYYYMMDD(calendarDate.date, createDate(productDate.date)),
      );
      let merged = null;
      if (this.today > calendarDate.date) {
        // Calendar date is passed
        merged = Object.assign({}, calendarDate);
      } else {
        // Future date
        merged = Object.assign(
          {},
          productForDate && productForDate.product ? productForDate : openDateWithNoProducts,
          calendarDate,
        );
      }
      mergedCalendar.push(merged);
    }

    return mergedCalendar;
  }

  /** Handlers start */
  onChangeMonth(month) {
    this.setState({
      currentDisplayedMonth: month,
    });
  }

  onClickDate(clickedDate) {
    if (!clickedDate.product || clickedDate.soldOut) {
      return;
    }

    this.setState({
      selectedDate: clickedDate.date,
      selectedProduct: this.transformProductToCartReadyProduct(clickedDate.product),
      currentDisplayedMonth: clickedDate.date,
    });
  }

  onChangeQuantity(amount) {
    this.setState({ quantity: amount });
  }

  onChangeAttribute(option) {
    const selectedAttribute = this.baseProduct.attributes.find(
      (attr) => attr.attributeId.toString() === option.toString(),
    );

    const selectedProduct = {
      ...this.baseProduct,
      price: selectedAttribute.price,
      attributeId: selectedAttribute.attributeId,
    };

    this.setState({
      selectedProduct: selectedProduct,
    });
  }
  /** Handlers end */

  transformProductToCartReadyProduct(product) {
    if (!product) {
      return null;
    }

    return {
      glkId: product.productId,
      jetshopId: product.jetshopId,
      timeslotId: product.timeSlots instanceof Array && product.timeSlots.length ? product.timeSlots[0].id : null,
      price: product.price,
      productType: product.productType,
      numberOfJetpassIncluded: product.numberOfJetpassIncluded,
      numberOfEntryTicketsIncluded: product.numberOfEntryTicketsIncluded,
    };
  }

  isProductValid() {
    if (!this.state.useBaseProduct) {
      return this.state.selectedProduct
        ? JetshopHelper.hasProductTimeSlots(this.state.selectedProduct.timeSlots)
        : false;
    }

    return this.baseProduct.price && JetshopHelper.isProductInStock(this.baseProduct.stockLevel);
  }

  enableAddToCart() {
    if (!this.productType) {
      return false;
    }

    if (this.productType === "BookableProduct" || (this.activateProductCalendar && !this.state.useBaseProduct)) {
      return this.state.selectedProduct ? true : false;
    }

    return true;
  }

  createAddedToCartNotification(globalContext) {
    NotificationHelper.addCartNotification({
      headline: this.addedToCartHeadline,
      body: this.addedToCartBody,
    });
    if (this.blockInOverlay) {
      globalContext.closeActiveOverlay();
    }
  }

  async addProductToCart(globalContext, product, shopServiceAddToCart) {
    const useShopService = SiteConfigurationStore.siteConfiguration.useNewCheckout;
    try {
      if (useShopService) {
        const slot =
          product.timeslotId && this.state.selectedDate
            ? {
                date: DateHelper.toStringYYYYMMDD(this.state.selectedDate),
                slotId: product.timeslotId,
              }
            : undefined;

        // Using string literal as JS cannot use TS enums
        const deliveryType = this.state.extendedProduct?.deliveryTypes?.[0]?.deliveryType ?? "Digital";

        shopServiceAddToCart({
          productId: Number(product.glkId),
          quantity: this.state.quantity,
          deliveryType,
          slot,
        });
      } else {
        await new Promise((r) => this.setState({ isAddingToCart: true }, r));

        this.context.addCartItem({
          jetshopId: parseInt(product.jetshopId ? product.jetshopId : this.jetshopId),
          glkId: product.glkId ? product.glkId : this.productData.glkId,
          quantity: this.state.quantity,
          productName: this.productName,
          productType: product.productType,
          attributeId: product.attributeId,
          reactivateCardNumber: undefined,
          reactivateEmail: undefined,
          timeslotId: product.timeslotId ? product.timeslotId : undefined,
          selectedDate: this.state.selectedDate ? this.state.selectedDate : undefined,
          numberOfJetpassIncluded: product.numberOfJetpassIncluded,
          numberOfEntryTicketsIncluded: product.numberOfEntryTicketsIncluded,
          productTimeLimit: product.productTimeLimit,
          onDoneAddToCart: () => this.createAddedToCartNotification(globalContext),
        });
      }

      await new Promise((r) => setTimeout(r, 500));

      this.setState({ quantity: 1, isAddingToCart: false });
      this.createAddedToCartNotification(globalContext);
    } catch (err) {
      console.log(err);
      await new Promise((r) => setTimeout(r, 500));
      this.setState({ isAddingToCart: false });
      if (this.blockInOverlay) {
        globalContext.closeActiveOverlay();
      }
    }
  }

  renderProductSelection(productType, activeProductCalendar) {
    if (activeProductCalendar && !this.state.useBaseProduct && this.state.usePreSelectedDate) {
      return this.renderPreSelectedProductDate();
    }

    if (activeProductCalendar && !this.state.useBaseProduct) {
      return this.renderProductCalendarSelection();
    }

    if (activeProductCalendar && this.state.useBaseProduct) {
      return this.renderOptOutProductCalendarSelection();
    }

    switch (productType) {
      case "Standard":
        return this.renderStandardSelection();
      case "BookableProduct":
        return this.renderProductCalendarSelection();
      case "NewCard":
        return this.renderCardAddToCart();
      default:
        return this.renderStandardSelection();
    }
  }

  renderCardAddToCart() {
    return null;
  }

  renderJetpassesIncluded(numberOfPasses, dateText) {
    if (typeof numberOfPasses === "number" && numberOfPasses > 0) {
      return (
        <div className={`${styles.numberOfJetpassWrapper} fade-in`}>
          <div className={styles.headlineRow}>
            <span className={`icon-check ${styles.checkIcon}`} />
            <p
              className={`${styles.jetpassIncludedText} body-s`}
            >{`${numberOfPasses} ${this.numberOfJetPassHeadline} ${dateText}`}</p>
          </div>
          <p className={`body-xs ${styles.numberOfJetpassParagraph}`}>{this.numberOfJetPassParagraph}</p>
        </div>
      );
    }

    return (
      <div className={[styles.numberOfJetpassWrapper, styles.noJetpass].join(" ")}>
        <p className={`${styles.noJetpassText} body-s`}>
          {this.noJetPassIncludedText} {dateText}
        </p>
      </div>
    );
  }

  renderAttributes() {
    const options = this.baseProduct.attributes
      .filter((attr) => attr.isBuyable)
      .map((attr) => {
        return {
          key: attr.attributeId,
          value: attr.attributeId,
          label: `${attr.price} ${this.currencyDisplayName}`,
        };
      });

    const currentSelected = options.find((option) => option.value === this.state.selectedProduct?.attributeId);
    return (
      <div className={styles.selectWrapper}>
        <SelectBox
          value={currentSelected ? currentSelected.value : ""}
          options={options}
          onChange={(name, value) => this.onChangeAttribute(value)}
          shouldShowCheckIcon={false}
        />
      </div>
    );
  }

  renderStandardSelection() {
    return (
      <div className={styles.standardAddToCartWrapper}>
        <h4 className={[styles.chooseDayText__standard].join(" ")}>{this.chooseAmountLabel}</h4>
        {this.state.productDataLoaded && isArray(this.baseProduct.attributes) && this.renderAttributes()}
        {this.renderQuantitySelector()}
      </div>
    );
  }

  renderOptOutProductCalendarSelection() {
    return (
      <div className={[styles.bookableWrapper].join(" ")}>
        <h2 className={[styles.chooseDayText].join(" ")}>{this.openTicketLabel}</h2>
        <p>{this.openTicketTextLabel}</p>
        <button
          onClick={() => {
            this.setState({ useBaseProduct: false });
          }}
          className={[styles.selectDateButtonOptOut].join(" ")}
        >
          {this.calendarOptInLabel}
        </button>
        {this.renderQuantitySelector()}
      </div>
    );
  }

  renderPreSelectedProductDate() {
    const dateText = DateHelper.toStringDDMonth(this.preSelectedProductDate);
    return (
      <div className={styles.bookableWrapper}>
        <h4 className={[styles.chooseDayText].join(" ")}>
          <span className={`${styles.calendarIcon} icon-calendar`} /> {`${this.selectedDateLabel}: ${dateText}`}
        </h4>
        <button
          onClick={() => {
            this.setState({ usePreSelectedDate: false });
          }}
          className={[styles.selectDateButtonPreSelected].join(" ")}
        >
          {this.selectOtherDateLabel}
        </button>
        {this.renderJetpassesIncluded(this.state.selectedProduct.numberOfJetpassIncluded, dateText)}
        {this.renderQuantitySelector()}
      </div>
    );
  }

  renderProductCalendarSelection() {
    return (
      <div className={[styles.bookableWrapper, "block--max-width-narrow"].join(" ")}>
        <h2 className={[styles.chooseDayText].join(" ")}>{this.selectDayForVisitLabel}</h2>
        {this.baseProduct.allowFlexibleProduct && (
          <button
            onClick={() => {
              this.setState({ useBaseProduct: true });
            }}
            className={[styles.selectDateButton].join(" ")}
          >
            {this.calendarOptOutLabel}
          </button>
        )}
        <ProductPageCalendar
          currentDisplayedMonth={this.state.currentDisplayedMonth}
          isLoading={!this.state.bookableProductsLoaded}
          onChangeMonth={this.onChangeMonth}
          showServiceError={this.state.showServiceError}
          serviceErrorText={this.serviceErrorText}
          productCalendar={this.state.bookableProducts}
          selectedDate={this.state.selectedDate}
          onClickDate={this.onClickDate}
        />
        {!this.state.selectedDate && (
          <div className={`${styles.chooseDayToContinueWrapper} fade-in`}>
            <p className={styles.chooseDayToContinue}>{this.chooseDayToContinueLabel}</p>
          </div>
        )}
        {this.state.selectedProduct &&
          this.state.selectedDate &&
          this.renderJetpassesIncluded(
            this.state.selectedProduct.numberOfJetpassIncluded,
            DateHelper.toStringDDMonth(this.state.selectedDate),
          )}
        {this.state.selectedDate && this.state.selectedProduct && this.renderQuantitySelector()}
      </div>
    );
  }

  getCurrentProduct() {
    if (
      (this.activateProductCalendar && !this.state.useBaseProduct) ||
      (this.state.selectedProduct && this.state.selectedProduct.attributeId)
    ) {
      return this.state.selectedProduct;
    }

    return this.baseProduct;
  }

  renderQuantitySelector() {
    const product = this.getCurrentProduct();

    return (
      <div className={styles.productSummary}>
        <QuantitySelector
          wrapperClassName={["fade-in", styles.quantityWrapper].join(" ")}
          quantity={this.state.quantity}
          productName="st"
          minQuantity={product.minimumQuantity ? product.minimumQuantity : 1}
          maxQuantity={
            this.props.maxNumberOfProductsPerPurchase
              ? this.props.maxNumberOfProductsPerPurchase
              : product.timeSlotQuantity
              ? product.timeSlotQuantity
              : this.baseProduct.stockLevel
          }
          description={product.price ? `a ${product.price} kr` : undefined}
          onQuantityChanged={(newQuantity) => this.onChangeQuantity(newQuantity)}
        />
      </div>
    );
  }

  renderCartActionWrapper(product) {
    const totalValue = product ? product.price * this.state.quantity : "-";
    return (
      <div className={[styles.cartActionWrapper, "fade-in"].join(" ")}>
        <div className={styles.totalWrapper}>
          <p className={styles.totalPrice}>
            {this.totalValueLabel} <AnimationPop content={totalValue} /> {this.currencyDisplayName}
          </p>
        </div>
        <div className={styles.addToCart}>
          <ShopServiceCartContextConsumer>
            {({ shopServiceContext: { addCartItem }, globalContext }) => (
              <PrimaryButton
                label={this.state.isAddingToCart ? "" : this.addToCartLabel}
                iconLeft={this.state.isAddingToCart ? "icon-spinner_one" : undefined}
                className={[
                  "waves-effect",
                  styles.addToCartButton,
                  this.state.isAddingToCart ? styles.isLoading : "",
                ].join(" ")}
                isFullWidth
                onClick={() => this.addProductToCart(globalContext, product, addCartItem)}
                isDisabled={!this.isProductValid() || this.state.isAddingToCart || !this.enableAddToCart()}
              />
            )}
          </ShopServiceCartContextConsumer>
        </div>
      </div>
    );
  }

  renderOptionalLinkButton() {
    return (
      <div className={[styles.optionalWrapper, "block--max-width-narrow"].join(" ")}>
        <PrimaryButtonLink
          label={this.optionalLinkLabelText}
          url={this.optionalLinkUrl}
          className={`waves-effect ${styles.optionalButton}`}
        />
      </div>
    );
  }

  render() {
    const product = this.getCurrentProduct();
    const hasJetshop = this.productData && !this.productData.isEmpty;

    return (
      <section
        className={[
          "block block--product-block",
          this.componentTheme ? `component-theme-${this.componentTheme}` : "",
        ].join(" ")}
        data-contentful-id={this.props.contentful_id}
      >
        <div className={styles.productWrapper}>
          <div className={styles.imageWrapper}>
            <div className={styles.imageInnerWrapper}>
              <UiBkgImage
                className={styles.image}
                imageUrlForSmallDevice={this.productImage}
                imageUrlForLargeDevice={this.productImage}
                smallDeviceImageMaxWidth={1200}
                largeDeviceImageMaxWidth={2560}
              />
            </div>
            <div className={styles.labelWrapper}>
              {this.label && (
                <SpeechBubbleProductListing
                  title={this.label}
                  enableBubbleArrowDesktop={true}
                  speechBubbleInverseColors={true}
                  componentTheme={this.lableComponentTheme}
                  bubbleBelongsTo="2"
                />
              )}
            </div>
          </div>
          <div
            className={[
              styles.infoWrapper,
              "block--max-width-narrow",
              this.label ? styles.extraMarginTopMobile : "",
            ].join(" ")}
          >
            <ProductInfo
              currencyDisplayName={this.currencyDisplayName}
              baseProduct={this.baseProduct}
              description={this.description}
              styles={styles}
              customCtaText={this.customCtaText}
              customPriceText={this.customPriceText}
              hasJetshop={hasJetshop}
              productName={this.productName}
              readMoreBody={this.readMoreBody}
              readMoreLabel={this.readMoreLabel}
            />

            {this.optionalLinkLabelText && this.optionalLinkUrl ? (
              this.renderOptionalLinkButton()
            ) : (
              <>
                {hasJetshop && this.renderProductSelection(this.productType, this.activateProductCalendar)}
                {hasJetshop && this.isProductValid() && this.enableAddToCart() && this.renderCartActionWrapper(product)}
              </>
            )}
          </div>
        </div>
      </section>
    );
  }
}

ProductBlock.contextType = CartContext;

export const fragmentProductBlock = graphql`
  fragment ProductBlock on ContentfulProductBlock {
    contentful_id
    id
    componentTheme
    internalTitle
    node_locale
    productData {
      glkId
      jetshopId
      productType
      hasAttributes
      relatedProducts
      additionalProducts
      isEmpty
    }
    productName
    productImage {
      file {
        url
      }
      gatsbyImageData(layout: FIXED, width: 228, height: 128, placeholder: NONE, quality: 100, resizingBehavior: FILL)
    }
    productPageLink {
      slug
    }
    cartThumbnail {
      file {
        url
      }
    }
    shortDescription
    description {
      childMarkdownRemark {
        html
      }
    }
    readMoreLabel
    readMoreText {
      childMarkdownRemark {
        html
      }
    }
    label
    labelComponentTheme
    tags {
      id
      title
    }
    optionalLinkLabelText
    optionalLinkUrl
    addedToCartHeadline
    addedToCartBody
    customPriceText
    customCtaText
    activateProductCalendar
    numberOfEntryTicketsIncluded
    numberOfJetpassIncluded
    serviceErrorText
    maxNumberOfProductsPerPurchase
    openInOverlay
    purchasableOutsideEntryTime
    purchaseFlowCustomPriceText
  }
`;

export const fragmentProductBlockStore = graphql`
  fragment ProductBlockStore on ContentfulProductBlock {
    id
    node_locale
    internalTitle
    componentTheme
    productName
    productNameMyPages
    shortDescription
    upsellText {
      upsellText
    }
    upsellRichText {
      raw
    }
    productImage {
      file {
        url
      }
      gatsbyImageData(layout: FIXED, width: 228, height: 128, placeholder: NONE, quality: 100, resizingBehavior: FILL)
    }
    cartThumbnail {
      file {
        url
      }
    }
    productData {
      glkId
      jetshopId
      productType
      hasAttributes
      relatedProducts
      additionalProducts
      isEmpty
    }
    customPriceText
    productPageLink {
      slug
    }
    tags {
      title
    }
    optionalLinkLabelText
    optionalLinkUrl
    numberOfEntryTicketsIncluded
    numberOfJetpassIncluded
    myPagesDescription
    myPageTheme
    needsTimeSlotBooking
    seasonPassVisitorCategory
    purchasableOutsideEntryTime
    purchaseFlowCustomPriceText
  }
`;
